import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Company } from '../../../../../common/src/domain/company/companyTableSchema';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchGet, openApiFetchPost } from '../../../store/openApiFetch';
import { useOpenApiFetch } from '../../../store/useOpenApiFetch';
import { reactSelectDefaultStyles } from '../../../utils/reactSelectDefaultStyles';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import { Modal } from '../../common/Modal/Modal';
import {
    Content,
    PanelBody,
    PanelWithoutForm,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Topbar,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../../elements';
import { Spinner } from '../../spinners';

export function InspectionFormCompanies(props: { params: { id?: string } }) {
    const t = useTranslate();

    const inspectionFormId = props.params.id || '';
    const { data, isLoading, reload } = useOpenApiFetch(
        '/api/settings/inspection-forms/{inspectionFormId}/companies',
        'get',
        { inspectionFormId },
        [],
    );

    const [isAddModalOpen, setIsAddModelOpen] = useState(false);

    const removeCompany = (companyId: string) => async () => {
        await openApiFetchDelete('/api/settings/inspection-forms/{inspectionFormId}/companies/{companyId}', { inspectionFormId, companyId });
        await reload();
        setIsAddModelOpen(false);
    };

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title={t('inspectionForms.title')} icon="fa-cogs" />
                    <TopbarCrumb>{t('common.companies')}</TopbarCrumb>
                </TopbarLeft>

                <TopbarRight>
                    <button className="btn btn-sm fw600" onClick={() => setIsAddModelOpen(true)}>
                        <span className="fa fa-plus mr5" />
                        {t('common.addItem', t('common.company'))}
                    </button>
                </TopbarRight>
            </Topbar>

            <Content>
                <Tray>
                    <PanelWithoutForm>
                        <PanelBody noPadding>
                            <Table hover>
                                <TableHead>
                                    <TableHeadRow light>
                                        <TableHeader>{t('common.company')}</TableHeader>
                                        <TableHeader align="right">&nbsp; </TableHeader>
                                    </TableHeadRow>
                                </TableHead>
                                {isLoading || !data ? (
                                    <TableBody>
                                        <TableRow>
                                            <TableData colSpan={2}>
                                                <Spinner />
                                            </TableData>
                                        </TableRow>
                                    </TableBody>
                                ) : (
                                    <TableBody>
                                        {data.map((company) => (
                                            <TableRow key={company.id}>
                                                <TableData>{company.name}</TableData>
                                                <TableData>
                                                    <IconRemoveButton
                                                        onClick={removeCompany(company.id)}
                                                        removeQuestion={t('common.confirmDelete', company.name)}
                                                    />
                                                </TableData>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                )}
                            </Table>
                        </PanelBody>
                    </PanelWithoutForm>
                </Tray>
            </Content>

            {isAddModalOpen && (
                <AddCompanyModal
                    inspectionFormId={inspectionFormId}
                    existingIds={data?.map((company) => company.id) ?? []}
                    onAdd={async () => {
                        await reload();
                        setIsAddModelOpen(false);
                    }}
                    onCancel={() => setIsAddModelOpen(false)}
                />
            )}
        </>
    );
}

interface IAddCompanyModalProps {
    inspectionFormId: string;
    existingIds: string[];
    onCancel: () => void;
    onAdd: () => void;
}

function AddCompanyModal({ inspectionFormId, existingIds, onCancel, onAdd }: IAddCompanyModalProps) {
    const t = useTranslate();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [value, setValue] = useState<string | undefined>(undefined);

    useEffect(() => {
        openApiFetchGet('/api/companies', { isClient: '1' })
            .then((result) => setCompanies((result as any).items))
            .catch((err) => console.error(err))
            .finally(() => setIsLoading(false));
    }, []);

    const onSubmit = async () => {
        if (!value) {
            return;
        }

        await openApiFetchPost(
            '/api/settings/inspection-forms/{inspectionFormId}/companies/{companyId}',
            { inspectionFormId, companyId: value },
            undefined,
        );

        onAdd();
    };

    return (
        <Modal id="add-company-modal" closeOnBackdropClick={false}>
            <Modal.Title>{t('common.addItem', t('common.company'))}</Modal.Title>

            <Modal.Content isScrollBarVisible={false} style={{ height: '50px' }}>
                <Select
                    isDisabled={isLoading}
                    options={companies
                        .filter((company) => !existingIds.includes(company.id))
                        .map((company) => ({ value: company.id, label: company.name }))}
                    onChange={(option) => setValue(option?.value)}
                    styles={{
                        ...reactSelectDefaultStyles,
                        container: () => ({ width: '100%', position: 'relative' }),
                    }}
                />
            </Modal.Content>

            <Modal.Actions>
                <div style={{ display: 'flex', justifyContent: 'end', gap: '1rem', marginRight: '1rem' }}>
                    <button type="button" className="btn btn-sm btn-default" onClick={onCancel}>
                        {t('common.cancel')}
                    </button>
                    <button type="button" className="btn btn-sm btn-success" disabled={!value} onClick={onSubmit}>
                        {t('common.addItem', t('common.company'))}
                    </button>
                </div>
            </Modal.Actions>
        </Modal>
    );
}
